import { defineMessages, FormattedMessage } from 'react-intl';
import Link from '@atlaskit/link';
import { Box, Text, Stack } from '@atlaskit/primitives';
import { useInfo, useToken } from '../../../Flow/ConsentContextProvider';

const messages = defineMessages({
  privacyAndEulaProvided: {
    id: 'oauth.bitbucket.privacyAndEulaProvided',
    defaultMessage:
      'By installing the app, you agree to the {privacyPolicyLink} and {termsOfUseLink} provided by {clientName}.',
    description:
      'Legal agreement text shown when both privacy policy and terms of use links are provided by the vendor',
  },
  onlyPrivacy: {
    id: 'oauth.bitbucket.onlyPrivacy',
    defaultMessage:
      'By installing the app, you agree to the {privacyPolicyLink} provided by {clientName}.',
    description:
      'Legal agreement text shown when only the privacy policy link is provided by the vendor',
  },
  onlyEula: {
    id: 'oauth.bitbucket.onlyEula',
    defaultMessage:
      "This 3rd party vendor has not provided a privacy policy. Atlassian's privacy policy is not applicable to the use of this app. {termsOfUseLink} to see the terms of use provided by {clientName}.",
    description:
      'Disclaimer text shown when only the Terms of Use (EULA) link is provided by the vendor',
  },
  noneProvided: {
    id: 'oauth.bitbucket.noneProvided',
    defaultMessage:
      "This 3rd party vendor has not provided a privacy policy or terms of use. Atlassian's Privacy Policy is not applicable to the use of this app.",
    description:
      'Fallback legal disclaimer shown when neither privacy policy nor terms of use links are provided',
  },
  privacyLinkText: {
    id: 'oauth.bitbucket.privacyLinkText',
    defaultMessage: 'privacy policy',
    description: 'The clickable text for the vendor privacy policy link',
  },
  eulaLinkText: {
    id: 'oauth.bitbucket.eulaLinkText',
    defaultMessage: 'terms of use',
    description: 'The clickable text for the vendor terms of use link',
  },
  eulaClickHere: {
    id: 'oauth.bitbucket.eulaClickHere',
    defaultMessage: 'Click here',
    description:
      'Alternative link text used to direct users to the vendor terms of use',
  },
});

const LegalDisclaimer: React.FC = () => {
  const { clientName, redirectUrl } = useToken();
  const { client } = useInfo();
  const { appEulaLink, appPrivacyPolicyLink, callbacks } = client ?? {};

  const privacyPolicyLink = appPrivacyPolicyLink && (
    <Link href={appPrivacyPolicyLink}>
      <FormattedMessage {...messages.privacyLinkText} />
    </Link>
  );

  const renderLegalDisclaimer = () => {
     const getLegalMessage = () => {
       if (appPrivacyPolicyLink && appEulaLink) {
         return (
           <FormattedMessage
             {...messages.privacyAndEulaProvided}
             values={{
               clientName,
               privacyPolicyLink,
               termsOfUseLink: (
                 <Link href={appEulaLink}>
                   <FormattedMessage {...messages.eulaLinkText} />
                 </Link>
               ),
             }}
           />
         );
       }

       if (appPrivacyPolicyLink) {
         return (
           <FormattedMessage
             {...messages.onlyPrivacy}
             values={{ clientName, privacyPolicyLink }}
           />
         );
       }

       if (appEulaLink) {
         return (
           <FormattedMessage
             {...messages.onlyEula}
             values={{
               clientName,
               termsOfUseLink: (
                 <Link href={appEulaLink}>
                   <FormattedMessage {...messages.eulaClickHere} />
                 </Link>
               ),
             }}
           />
         );
       }

       return <FormattedMessage {...messages.noneProvided} />;
     };

    const getCallbackURL = () => {
      const tokenRedirectCallback = redirectUrl?.callback
       if (tokenRedirectCallback != null && tokenRedirectCallback != "") {
         // Use the token's redirectUrl if provided (and not null)
         return tokenRedirectCallback
       } else if (callbacks && callbacks?.length > 0) {
         // Otherwise fallback to the first callback in the list from the consent info provider
         return callbacks[0]
       } else {
         return null
       }
    }
    const buildCallbackURLText = () => {
      const url = getCallbackURL()
      if (url){
        return (
          <Text color={"color.text.subtle"}>You will be redirected to: {url}</Text>
        )
      }else {
        return null;
      }
    }

    return (
      <Stack>
        <Text color={"color.text.subtle"}>{getLegalMessage()}</Text>
        {buildCallbackURLText()}
      </Stack>
    );
  };

  return <Box>{renderLegalDisclaimer()}</Box>;
};

export default LegalDisclaimer;
